import { DISPENSER_ABI } from "@/config/abis/dispenser-abi";
import type { ChainData } from "@/constants/chain-details";
import { resolveChainFromId } from "@/utils/resolve-chain.util";
import { useEffect, useState } from "react";
import { readContract, waitForTransactionReceipt } from "wagmi/actions";
import { useAccount, useChainId, useConfig, useWriteContract } from "wagmi";
import { useQuery } from "wagmi/query";
import { convertAndOperate } from "@/utils/convert-eth-to-bless.util";
import { useUIStore } from "@/stores/ui-states.store";
import { isMainnet } from "@/constants/flags.constant";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import {
  showToast,
  showToastTxRevert,
  showToastTxSuccess,
} from "@/components/layout/toast/show-toast";
import { useManagedWriteContract } from "@/hooks/useManagedContractWrite.hook";
import { TOAST_TX_MSG } from "@/features/bridge/constants/toast-msgs.constants";

export interface DispenserResponse {
  destinations: string[];
  rate: bigint;
  minimumBless: bigint;
  maximumBless: bigint;
}

const useDispenser = () => {
  const chainId = useChainId();
  const config = useConfig();
  const { isConnected } = useAccount();

  const [ethInput, setEthInput] = useState<string>("");
  const [blessInput, setBlessInput] = useState<string>("");
  const [recipientChain, setRecipientChain] = useState<ChainData | undefined>(
    undefined,
  );
  const [rate, setRate] = useState<number>(0);

  //* contract write function with response handling
  const { writeContractAsyncWithTimeout, isSigningPending } =
    useManagedWriteContract({
      callback: {
        onSigningError: () => {
          showToast({
            toastContent: TOAST_TX_MSG.SIGN_ERROR.toastContent,
          });
        },
        onRevert: showToastTxRevert,
        onSuccess: (hash) => {
          setEthInput("");
          setBlessInput("");
          showToastTxSuccess(hash);
        },
      },
    });

  //* sign tx to recieve bless at given input value
  const requestBless = async (
    ethWei: bigint,
    blessWei: bigint,
    destinationChainId: bigint,
  ) => {
    // No values inputted
    if (!ethWei || !blessInput) {
      console.error("Missing input values");
      return;
    }

    try {
      const response = await writeContractAsyncWithTimeout({
        abi: DISPENSER_ABI,
        address: CONTRACT_ADDRESSES.DISPENSER,
        functionName: "requestBless",
        args: [blessWei, destinationChainId],
        value: ethWei,
      });

      const data = await waitForTransactionReceipt(config, {
        hash: response,
      });

      return data;
    } catch (error) {
      console.error("Error executing requestBless:", error);
      return;
    }
  };

  /**
   * should not use wagmi as it obfuscates the queryKey control
   * react query preventing the desired cache storage structure
   */
  //* replaces wagmi query wrapped read contract function
  const allSettingsQuery = useQuery({
    queryKey: ["allSettings", chainId],
    queryFn: async () =>
      await readContract(config, {
        abi: DISPENSER_ABI,
        address: CONTRACT_ADDRESSES.DISPENSER,
        functionName: "allSettings",
      }),
    select: (res) => {
      const data = res as readonly any[];
      if (!data.length) return undefined;

      return {
        destinations: data[0],
        rate: data[1],
        minimumBless: data[2],
        maximumBless: data[3],
      } as DispenserResponse;
    },
    enabled: isConnected, // only make fetches when wallet connected
  });

  //* every time the allSettingsQuery.data changes, update the recipient chain
  useEffect(() => {
    if (allSettingsQuery.data?.destinations.length) {
      const chain = resolveChainFromId(
        Number(allSettingsQuery.data.destinations[0]),
        isMainnet ? "mainnet" : "testnet",
      );
      if (chain) setRecipientChain(chain);
    } else if (!allSettingsQuery.data?.destinations.length) {
      setRecipientChain(undefined);
    }
  }, [allSettingsQuery.data, chainId]);

  //* reset input values when switching chains or network env
  useEffect(() => {
    setEthInput("");
    setBlessInput("");
  }, [chainId]);

  //* update the rate state everytime rate changes
  useEffect(() => {
    if (blessInput) {
      const ethWei = convertAndOperate(blessInput, rate.toString(), "multiply");
      setEthInput(ethWei.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate]);

  return {
    allSettingsQuery,
    requestBless,
    state: {
      recipientChain,
      setRecipientChain,
      ethInput,
      setEthInput,
      blessInput,
      setBlessInput,
      rate,
      setRate,
      isSigningPending,
    },
  };
};

export default useDispenser;
